import {
  useId,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';

import type {
  CheckboxFieldKitProps,
  CheckboxGroupFieldProps,
  FieldFormat,
  FieldKitProps,
} from '@/shared/forms/types';
import { cn } from '@/shared/utils/cn';
import { normaliseDecimalInput, toLatinDigits } from '@/shared/utils/digits';

import { Icon } from './Icon';

interface BaseProps {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
}

/*
 * DEF-007 — no focus classes here. A field's keyboard focus is the one R-050
 * ring (src/index.css: Focus Violet, 2px, 1px clear of the box, so it sits just
 * outside the 1px `ring-line` hairline); this used to add a second, box-shadow
 * ring of its own beside the global outline, and the two together drew a thick
 * band that matched neither.
 */
export const CONTROL_CLASSES =
  'block w-full min-h-12 sm:min-h-11 rounded-lg border-0 bg-card px-3 py-2 text-ink ring-1 ring-line ' +
  'placeholder:text-ink-disabled disabled:bg-disabled';

/**
 * UI-3 — read-only is not disabled (UI/UX Pro Max "read-only-distinction"): a
 * value one may read, select and copy sits on the sunken surface with a quiet
 * hairline, in ordinary ink. Disabled keeps its own grey (`disabled:bg-disabled`).
 * Applied by the fields below only, never inside CONTROL_CLASSES: the Combobox
 * uses a read-only input as its phone sheet's trigger.
 */
const READ_ONLY_CLASSES = 'bg-sunken ring-line-subtle cursor-default';

/**
 * A field's error, as R-050's FormField states it: the Critical mark and the
 * words together, never colour alone. Exported for the few controls that sit
 * outside a Field — a quantity in a table row, a rule condition — so their
 * errors read like every other one.
 */
export function FieldError({
  id,
  children,
  className,
}: {
  id?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      id={id}
      role="alert"
      className={cn('type-label flex items-start gap-1.5 text-critical-ink', className)}
    >
      <Icon name="alert" size={14} className="mt-px shrink-0" />
      {/* `auto`: a message in the other language keeps its own punctuation order. */}
      <span dir="auto" className="min-w-0">
        {children}
      </span>
    </p>
  );
}

/**
 * R-050 FormField: label above, the control, then the hint — or, when there
 * is one, the error in its place. The required mark is the Exception glyph R-050
 * draws; the requirement itself reaches a screen reader through the control's
 * own `required` and `aria-required` (DEC-05), so the label's accessible name
 * stays the label.
 *
 * UI-3 adds the business consequence (R-02a "Every approver on the route reads
 * this."), kept under the hint or the error: a consequence still holds while
 * the value is being corrected.
 *
 * Exported for a control that is not one of the fields below (the Combobox),
 * so it keeps the same frame. The control carries `id`, and points
 * `aria-describedby` at `${id}-hint` or, when there is an error, `${id}-error`,
 * then `${id}-consequence`.
 */
export function Wrapper({
  id,
  label,
  error,
  hint,
  required,
  consequence,
  warning,
  success,
  optional,
  children,
}: BaseProps &
  Pick<FieldKitProps, 'consequence' | 'warning' | 'success' | 'optional'> & {
    id: string;
    children: ReactNode;
  }) {
  const { t } = useTranslation();
  const hintId = hint && !error ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="type-ui block font-medium text-ink">
        {label}
        {required ? (
          <span className="ms-1 font-semibold text-exception-ink" aria-hidden="true">
            *
          </span>
        ) : null}
        {optional && !required ? (
          <>
            {' '}
            <span className="font-normal text-ink-muted">{t('shared.form.optional')}</span>
          </>
        ) : null}
      </label>
      {children}
      {error ? (
        <FieldError id={errorId}>{error}</FieldError>
      ) : hint ? (
        <p id={hintId} className="type-meta text-ink-muted">
          {hint}
        </p>
      ) : null}
      {consequence ? (
        <p id={`${id}-consequence`} className="type-meta text-ink-muted">
          {consequence}
        </p>
      ) : null}
      {!error && (warning || success) ? (
        <p
          id={`${id}-${warning ? 'warning' : 'success'}`}
          className={cn('type-meta', warning ? 'text-warning-ink' : 'text-success-ink')}
        >
          <span dir="auto">{warning || success}</span>
        </p>
      ) : null}
    </div>
  );
}

/** The ids a control's `aria-describedby` names, in reading order. */
function describedBy(
  id: string,
  {
    hint,
    error,
    consequence,
    unit,
    prefix,
    suffix,
    warning,
    success,
    extra,
  }: {
    hint?: string;
    error?: string;
    consequence?: string;
    unit?: boolean;
    prefix?: boolean;
    suffix?: boolean;
    warning?: string;
    success?: string;
    extra?: string;
  },
): string | undefined {
  return (
    cn(
      prefix && `${id}-prefix`,
      unit && `${id}-unit`,
      suffix && `${id}-suffix`,
      hint && !error && `${id}-hint`,
      error && `${id}-error`,
      consequence && `${id}-consequence`,
      !error && warning && `${id}-warning`,
      !error && !warning && success && `${id}-success`,
      extra,
    ) || undefined
  );
}

/** What each value preset does to the control (see FieldFormat). */
const FORMATS: Record<
  FieldFormat,
  {
    inputMode: InputHTMLAttributes<HTMLInputElement>['inputMode'];
    normalise?: (value: string) => string;
    autoCapitalize?: string;
  }
> = {
  code: { inputMode: 'text', autoCapitalize: 'characters' },
  digits: { inputMode: 'numeric', normalise: toLatinDigits },
  email: { inputMode: 'email', autoCapitalize: 'none' },
  tel: { inputMode: 'tel', normalise: toLatinDigits },
  url: { inputMode: 'url', autoCapitalize: 'none' },
  money: { inputMode: 'decimal', normalise: normaliseDecimalInput },
  quantity: { inputMode: 'decimal', normalise: normaliseDecimalInput },
};

/**
 * The attributes a value preset or a content language puts on the control.
 * A preset's VALUE is LTR while its alignment still follows the page — an
 * Arabic form keeps its right edge: under RTL, the LTR control aligns to its
 * own END (`rtl:text-end`), which is the page's start.
 */
function presetAttributes(format?: FieldFormat, contentLang?: 'ar' | 'en') {
  if (format) {
    const preset = FORMATS[format];

    return {
      attributes: {
        dir: 'ltr' as const,
        inputMode: preset.inputMode,
        spellCheck: false,
        autoCorrect: 'off',
        autoCapitalize: preset.autoCapitalize,
      },
      className: 'rtl:text-end',
      normalise: preset.normalise,
    };
  }

  if (contentLang) {
    return {
      attributes: {
        lang: contentLang,
        dir: contentLang === 'ar' ? ('rtl' as const) : ('ltr' as const),
      },
      className: undefined,
      normalise: undefined,
    };
  }

  return { attributes: {}, className: undefined, normalise: undefined };
}

/**
 * Latin digits (and, for an amount, a point for the Arabic decimal separator)
 * as the person types: the value the change handler reads is the server's form.
 */
function normalising(
  normalise: ((value: string) => string) | undefined,
  onChange: ((event: ChangeEvent<HTMLInputElement>) => void) | undefined,
) {
  if (!normalise) return onChange;

  return (event: ChangeEvent<HTMLInputElement>) => {
    const next = normalise(event.target.value);
    if (next !== event.target.value) event.target.value = next;
    onChange?.(event);
  };
}

/** `name` → `data-error-key`, and the field's message from `errors` unless `error` is given. */
function bound(
  name: string | undefined,
  error: string | undefined,
  errors: FieldKitProps['errors'],
) {
  return {
    error: error ?? (name ? errors?.[name]?.[0] : undefined),
    errorKey: name,
  };
}

function hasContent(node: ReactNode): boolean {
  return node !== undefined && node !== null && node !== false && node !== '';
}

/** The unit beside the value: an attached box at the inline end, isolated, read with the field. */
function Unit({ id, children }: { id: string; children: ReactNode }) {
  return (
    <span
      id={`${id}-unit`}
      className="type-ui inline-flex shrink-0 items-center rounded-e-lg bg-sunken px-3 text-ink-muted ring-1 ring-line"
    >
      <bdi>{children}</bdi>
    </span>
  );
}

function Adornment({
  id,
  side,
  children,
}: {
  id: string;
  side: 'prefix' | 'suffix';
  children: ReactNode;
}) {
  return (
    <span
      id={`${id}-${side}`}
      className={cn(
        'type-ui inline-flex shrink-0 items-center bg-sunken px-3 text-ink-muted ring-1 ring-line',
        side === 'prefix' ? 'rounded-s-lg' : 'rounded-e-lg',
      )}
    >
      <bdi>{children}</bdi>
    </span>
  );
}

export interface TextFieldProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'name' | 'prefix'>,
    BaseProps,
    FieldKitProps {
  /**
   * Draws a frame of the caller's own around the bare input: the field's outline,
   * its affordances and its Clear control, with the input inside (DateField —
   * DEF-024/027). The label above and the hint or error below stay the field's.
   */
  frame?: (input: ReactNode) => ReactNode;
}

export function TextField({
  label,
  error,
  hint,
  required,
  className,
  name,
  errors,
  consequence,
  unit,
  prefix,
  suffix,
  warning,
  success,
  loading,
  optional,
  format,
  contentLang,
  onChange,
  readOnly,
  frame,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: TextFieldProps) {
  const id = useId();
  const field = bound(name, error, errors);
  const preset = presetAttributes(format, contentLang);
  const withUnit = hasContent(unit);
  const withPrefix = hasContent(prefix);
  const withSuffix = hasContent(suffix);
  const adorned = withUnit || withPrefix || withSuffix;

  const input = (
    <input
      id={id}
      name={name}
      data-error-key={field.errorKey}
      aria-invalid={field.error ? true : undefined}
      aria-busy={loading || undefined}
      aria-describedby={describedBy(id, {
        hint,
        error: field.error,
        consequence,
        unit: withUnit,
        prefix: withPrefix,
        suffix: withSuffix,
        warning,
        success,
        extra: ariaDescribedBy,
      })}
      required={required}
      aria-required={required || undefined}
      readOnly={readOnly}
      {...preset.attributes}
      className={cn(
        CONTROL_CLASSES,
        preset.className,
        readOnly && READ_ONLY_CLASSES,
        adorned && 'min-w-0 flex-1',
        (withUnit || withSuffix) && 'rounded-e-none',
        withPrefix && 'rounded-s-none',
        field.error && 'ring-critical',
        className,
      )}
      onChange={normalising(preset.normalise, onChange)}
      {...rest}
    />
  );
  const control = frame ? frame(input) : input;

  return (
    <Wrapper
      id={id}
      label={label}
      error={field.error}
      hint={hint}
      required={required}
      consequence={consequence}
      warning={warning}
      success={success}
      optional={optional}
    >
      {adorned ? (
        <div className="flex items-stretch">
          {withPrefix ? (
            <Adornment id={id} side="prefix">
              {prefix}
            </Adornment>
          ) : null}
          {control}
          {withUnit ? <Unit id={id}>{unit}</Unit> : null}
          {withSuffix ? (
            <Adornment id={id} side="suffix">
              {suffix}
            </Adornment>
          ) : null}
        </div>
      ) : (
        control
      )}
    </Wrapper>
  );
}

/**
 * A document picker in the same frame as every other field: the label above,
 * the platform's own file control — its picker and its keyboard behaviour kept —
 * then the hint or the error. R-050 defines no upload component; this is the
 * native control dressed like its neighbours, in one place instead of four.
 */
export function FileField({ className, ...rest }: Omit<TextFieldProps, 'type'>) {
  return (
    <TextField
      type="file"
      className={cn(
        'py-2 file:me-3 file:min-h-8 file:rounded-md file:border-0 file:bg-sunken file:px-3 file:text-sm file:text-ink',
        className,
      )}
      {...rest}
    />
  );
}

export interface TextAreaFieldProps
  extends
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'name'>,
    BaseProps,
    Omit<FieldKitProps, 'unit' | 'format' | 'prefix' | 'suffix'> {}

/**
 * A labelled multi-line answer — a decision's reason, a return's instruction.
 * The label sits above the control, hint and error are wired to it, and the
 * box starts tall enough to read what was written back (R-050 Textarea).
 */
export function TextAreaField({
  label,
  error,
  hint,
  required,
  className,
  rows = 3,
  name,
  errors,
  consequence,
  contentLang,
  warning,
  success,
  loading,
  optional,
  readOnly,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: TextAreaFieldProps) {
  const id = useId();
  const field = bound(name, error, errors);
  const preset = presetAttributes(undefined, contentLang);

  return (
    <Wrapper
      id={id}
      label={label}
      error={field.error}
      hint={hint}
      required={required}
      consequence={consequence}
      warning={warning}
      success={success}
      optional={optional}
    >
      <textarea
        id={id}
        rows={rows}
        name={name}
        data-error-key={field.errorKey}
        dir="auto"
        aria-invalid={field.error ? true : undefined}
        aria-busy={loading || undefined}
        aria-describedby={describedBy(id, {
          hint,
          error: field.error,
          consequence,
          warning,
          success,
          extra: ariaDescribedBy,
        })}
        required={required}
        aria-required={required || undefined}
        readOnly={readOnly}
        {...preset.attributes}
        className={cn(
          CONTROL_CLASSES,
          'resize-y',
          readOnly && READ_ONLY_CLASSES,
          field.error && 'ring-critical',
          className,
        )}
        {...rest}
      />
    </Wrapper>
  );
}

export interface SelectFieldProps
  extends
    Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'name' | 'prefix'>,
    BaseProps,
    Omit<FieldKitProps, 'format' | 'contentLang'> {
  children: ReactNode;
}

export function SelectField({
  label,
  error,
  hint,
  required,
  className,
  children,
  name,
  errors,
  consequence,
  unit,
  prefix,
  suffix,
  warning,
  success,
  loading,
  optional,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: SelectFieldProps) {
  const id = useId();
  const field = bound(name, error, errors);
  const withUnit = hasContent(unit);
  const withPrefix = hasContent(prefix);
  const withSuffix = hasContent(suffix);
  const adorned = withUnit || withPrefix || withSuffix;

  const select = (
    <select
      id={id}
      name={name}
      data-error-key={field.errorKey}
      aria-invalid={field.error ? true : undefined}
      aria-busy={loading || undefined}
      aria-describedby={describedBy(id, {
        hint,
        error: field.error,
        consequence,
        unit: withUnit,
        prefix: withPrefix,
        suffix: withSuffix,
        warning,
        success,
        extra: ariaDescribedBy,
      })}
      required={required}
      aria-required={required || undefined}
      className={cn(
        CONTROL_CLASSES,
        adorned && 'min-w-0 flex-1',
        (withUnit || withSuffix) && 'rounded-e-none',
        withPrefix && 'rounded-s-none',
        field.error && 'ring-critical',
        className,
      )}
      {...rest}
    >
      {children}
    </select>
  );

  return (
    <Wrapper
      id={id}
      label={label}
      error={field.error}
      hint={hint}
      required={required}
      consequence={consequence}
      warning={warning}
      success={success}
      optional={optional}
    >
      {adorned ? (
        <div className="flex items-stretch">
          {withPrefix ? (
            <Adornment id={id} side="prefix">
              {prefix}
            </Adornment>
          ) : null}
          {select}
          {withUnit ? <Unit id={id}>{unit}</Unit> : null}
          {withSuffix ? (
            <Adornment id={id} side="suffix">
              {suffix}
            </Adornment>
          ) : null}
        </div>
      ) : (
        select
      )}
    </Wrapper>
  );
}

export interface CheckboxFieldProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type' | 'name'>,
    CheckboxFieldKitProps {
  label: string;
  hint?: string;
}

export function CheckboxField({
  label,
  hint,
  error,
  name,
  className,
  ...rest
}: CheckboxFieldProps) {
  const id = useId();

  /*
   * The box is 20px because that is the size a checkbox should look. The TARGET
   * is the whole row: the label is already bound to the input, so wrapping both
   * in a 44px-high flex row means a thumb can land anywhere along it. Measuring
   * the input alone reported 20×20 and it genuinely was — the row around it is
   * what makes it reachable.
   */
  const row = (
    <div className="flex items-start gap-3">
      <label
        htmlFor={id}
        className="type-ui flex min-h-11 flex-1 items-start gap-3 py-1.5 font-medium text-ink"
      >
        <input
          id={id}
          type="checkbox"
          name={name}
          data-error-key={name}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            'mt-0.5 size-5 shrink-0 rounded border-line text-brand-600 focus:ring-focus',
            className,
          )}
          {...rest}
        />
        <span>
          {label}
          {hint ? <span className="type-meta block text-ink-muted">{hint}</span> : null}
        </span>
      </label>
    </div>
  );

  // Without an error the markup is exactly what it was, so a row in a list keeps its rhythm.
  if (!error) return row;

  return (
    <div className="space-y-1">
      {row}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </div>
  );
}

/**
 * Several checkboxes answering one question (a user's roles, the purchasing
 * nodes a source serves): one fieldset whose legend is the question, the
 * R-050 required mark, then the hint or the error for the group as a whole.
 * `name` is the API's path for the list, so a refusal of `role_ids` leads the
 * reader to the first box (focusErrorTarget: the checked one, else the first).
 */
export function CheckboxGroupField({
  legend,
  name,
  hint,
  error,
  required,
  children,
}: CheckboxGroupFieldProps) {
  const id = useId();

  return (
    <fieldset
      data-error-key={name}
      aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
      className="min-w-0 space-y-1.5"
    >
      <legend className="type-ui mb-1 font-medium text-ink">
        {legend}
        {required ? (
          <span className="ms-1 font-semibold text-exception-ink" aria-hidden="true">
            *
          </span>
        ) : null}
      </legend>
      {error ? (
        <FieldError id={`${id}-error`}>{error}</FieldError>
      ) : hint ? (
        <p id={`${id}-hint`} className="type-meta text-ink-muted">
          {hint}
        </p>
      ) : null}
      <div className="space-y-0.5">{children}</div>
    </fieldset>
  );
}
